import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
// GOOGLE CALENDAR DISABLED
// import { deleteCalendarEvent } from "../../services/googleCalendar";
import "./MeetingDetailsModal.css";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";

function MeetingDetailsModal({
  isOpen,
  onClose,
  recruit,
  meeting,
  onReschedule,
  onLogMeeting,
  onMeetingCancelled,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelNotes, setCancelNotes] = useState("");
  const [cancelling, setCancelling] = useState(false);

  if (!isOpen || !meeting) return null;

  const formatDate = dateTime => {
    return new Date(dateTime).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = dateTime => {
    return new Date(dateTime).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getAttendeeStatus = attendee => {
    const statusMap = {
      needsAction: { text: "Pending", colorClass: "orange", icon: "schedule" },
      accepted: {
        text: "Accepted",
        colorClass: "success",
        icon: "check_circle",
      },
      declined: { text: "Declined", colorClass: "error", icon: "cancel" },
      tentative: { text: "Tentative", colorClass: "info", icon: "help" },
    };

    return (
      statusMap[attendee.responseStatus] || {
        text: "Unknown",
        colorClass: "secondary",
        icon: "help_outline",
      }
    );
  };

  const handleCancelMeeting = async () => {
    if (!recruit || !meeting || !cancelNotes.trim()) {
      showError("Please provide a reason for cancelling");
      return;
    }

    setCancelling(true);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        setCancelling(false);
        return;
      }

      // Close modal immediately for better UX
      showSuccess("Cancelling meeting...");
      handleClose();

      // Continue operations in background
      // GOOGLE CALENDAR DISABLED - Only saving cancellation to database
      // // Try to delete from Google Calendar (don't fail if this errors)
      // if (meeting.eventId) {
      //   try {
      //     await deleteCalendarEvent(meeting.eventId);
      //   } catch (calError) {
      //     // Continue anyway - we still want to save the cancellation
      //   }
      // }

      // Save cancellation to database history (now happens in background)
      const response = await fetch(
        getApiEndpoint(`/v1/recruiting/leads/${recruit.id}/cancel-meeting`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            meeting_date: meeting.time,
            notes: cancelNotes.trim(),
          }),
        }
      );

      if (response.ok) {
        // Wait for response to fully complete
        await response.json();

        // Add small delay to ensure database transaction is committed
        await new Promise(resolve => setTimeout(resolve, 500));

        // Call the callback to refresh the data
        if (onMeetingCancelled) {
          onMeetingCancelled(recruit);
        }
      } else {
        showError("Failed to save cancellation");
      }
    } catch (error) {
      handleGenericError(error, "Failed to cancel meeting");
    } finally {
      setCancelling(false);
    }
  };

  const handleClose = () => {
    setCancelNotes("");
    setShowCancelForm(false);
    onClose();
  };

  return (
    <div className="meeting-details-overlay" onClick={handleClose}>
      <div className="meeting-details-modal" onClick={e => e.stopPropagation()}>
        <button className="meeting-details-close-btn" onClick={handleClose}>
          ×
        </button>

        <div className="meeting-details-header">
          <h2>Meeting Details</h2>
          {recruit && (
            <p className="meeting-details-subtitle">{recruit.name}</p>
          )}
        </div>

        <div className="meeting-details-content">
          {/* Date and Time */}
          <div className="meeting-info-section">
            <div className="meeting-info-row">
              <span className="meeting-info-label">
                <span className="material-icons">event</span>
                Date:
              </span>
              <span className="meeting-info-value">
                {formatDate(meeting.time)}
              </span>
            </div>
            <div className="meeting-info-row">
              <span className="meeting-info-label">
                <span className="material-icons">schedule</span>
                Time:
              </span>
              <span className="meeting-info-value">
                {formatTime(meeting.time)}
              </span>
            </div>
            <div className="meeting-info-row">
              <span className="meeting-info-label">
                <span className="material-icons">timer</span>
                Duration:
              </span>
              <span className="meeting-info-value">{meeting.duration} min</span>
            </div>
          </div>

          {/* Attendees Status */}
          {meeting.attendees && meeting.attendees.length > 0 && (
            <div className="meeting-attendees-section">
              <h3>Attendees</h3>
              <div className="attendees-list">
                {meeting.attendees.map((attendee, index) => {
                  const status = getAttendeeStatus(attendee);

                  // Parse manager names from managerNames field (comma-separated)
                  const managerNames = meeting.managerNames
                    ? meeting.managerNames.split(",").map(name => name.trim())
                    : meeting.managerName
                      ? [meeting.managerName]
                      : [];

                  // Try to match this attendee to a manager name
                  const managerName =
                    managerNames[index] ||
                    attendee.displayName ||
                    attendee.email;

                  return (
                    <div key={index} className="attendee-item">
                      <div className="attendee-info">
                        <span className="attendee-name">{managerName}</span>
                        {attendee.email && (
                          <span className="attendee-email">
                            {attendee.email}
                          </span>
                        )}
                      </div>
                      <div
                        className={`meeting-details-attendee-status meeting-details-status-${status.colorClass}`}
                      >
                        <span className="material-icons meeting-details-status-icon">
                          {status.icon}
                        </span>
                        <span className="meeting-details-status-text">
                          {status.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(!meeting.attendees || meeting.attendees.length === 0) && (
            <div className="no-attendees">
              <p>No additional attendees for this meeting</p>
            </div>
          )}

          {/* Cancel Form */}
          {showCancelForm && (
            <div className="cancel-meeting-form">
              <h3>Cancel Meeting</h3>
              <p>Please provide a reason for cancelling this meeting</p>
              <textarea
                placeholder="Reason for cancellation..."
                value={cancelNotes}
                onChange={e => setCancelNotes(e.target.value)}
                rows={4}
                className="cancel-notes-textarea"
              />
              <div className="cancel-form-actions">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowCancelForm(false);
                    setCancelNotes("");
                  }}
                  disabled={cancelling}
                >
                  Back
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleCancelMeeting}
                  disabled={cancelling || !cancelNotes.trim()}
                >
                  Confirm Cancellation
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="meeting-details-footer">
          {!showCancelForm ? (
            <>
              <button
                className="btn btn-md btn-outline-primary"
                onClick={() => {
                  handleClose();
                  if (onReschedule) {
                    onReschedule(recruit);
                  }
                }}
                style={{ flex: 1 }}
              >
                <span className="material-icons" style={{ fontSize: "1.2rem" }}>
                  event_repeat
                </span>
                <span>Reschedule</span>
              </button>
              <button
                className="btn btn-md btn-danger"
                onClick={() => setShowCancelForm(true)}
                style={{ flex: 1 }}
              >
                <span className="material-icons" style={{ fontSize: "1.2rem" }}>
                  event_busy
                </span>
                <span>Cancel Meeting</span>
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default MeetingDetailsModal;
